/**
 * useFlo — 의미 그래프를 모델·배치·엣지까지 한 번에 계산하는 훅.
 * WeakMap(graph -> Map(optsKey -> 결과)) 캐시라, 같은 graph·옵션을 미니(썸네일)와 다이얼로그가 각각 써도 실제 계산은 한 번만 돈다.
 * 중립 leaf — pages/** 를 import 하지 않는다.
 */
import { useMemo } from "react";

import { buildFloModel, DEFAULT_LANE_TYPE_ORDER } from "@plannel/components/flo/floModel";
import { computeGeometry, buildEdges } from "@plannel/components/flo/floGeometry";
import { textWidth } from "@plannel/utils/text-measure-util";
import { useFloFonts } from "@plannel/components/flo/floTokens";

const flowCache = new WeakMap();

/** 접기 한도·레인 유형 순서·측정 글꼴과 그 적재 세대를 한 문자열로 묶는다 — 캐시 Map 의 2차 키다. */
function optsKeyOf(foldLimit, laneTypeOrder, fonts) {
  return `${foldLimit}|${(laneTypeOrder || []).join(",")}|${fonts.codeFont}|${fonts.epoch}`;
}

export default function useFlo(graph, opts = {}) {
  const { foldLimit = 6, laneTypeOrder = DEFAULT_LANE_TYPE_ORDER } = opts;
  const fonts = useFloFonts();

  return useMemo(() => {
    if (!graph) return { model: null, geometry: null, edges: null, fonts };

    let byOpts = flowCache.get(graph);
    if (!byOpts) {
      byOpts = new Map();
      flowCache.set(graph, byOpts);
    }
    const key = optsKeyOf(foldLimit, laneTypeOrder, fonts);
    let hit = byOpts.get(key);
    if (!hit) {
      const model = buildFloModel(graph, { foldLimit, laneTypeOrder });
      const geometry = computeGeometry(model);
      const measure = (s) => textWidth(s, fonts.codeFont);
      const edges = buildEdges(model, geometry, measure);
      hit = { model, geometry, edges };
      byOpts.set(key, hit);
    }
    return { ...hit, fonts };
  }, [graph, foldLimit, laneTypeOrder, fonts]);
}
